import CT from '@marketplace-ai/tags';
import classNames from 'classnames';
import { Pill } from '~/components';
import { dateStringFormatter } from '~/features/utils';
import { getContractRoles } from '../contractParties';
import { ContractListItem } from '../types';
import { ContractStatusBadge } from './ContractStatusBadge';

const TAGS = CT.employer.contracts.contractList;

interface ContractCardProps {
  contract: ContractListItem;
  isSelected: boolean;
  showRoleBadge?: boolean;
  isProviderView?: boolean;
  currentEmployerId?: number;
  onClick: () => void;
}

export const ContractCard = ({
  contract,
  isSelected,
  showRoleBadge = false,
  isProviderView = false,
  currentEmployerId,
  onClick,
}: ContractCardProps) => {
  const isListingSide =
    currentEmployerId != null && contract.listingEmployerId === currentEmployerId;
  const roles = getContractRoles(contract);
  const role = isListingSide ? roles.listing : roles.contracted;

  return (
    <button
      type="button"
      className={classNames(
        'p-3 border rounded-lg mb-2 w-full text-left cursor-pointer transition-colors',
        isSelected
          ? 'bg-primary-t-900 border-primary-t-700 shadow-sm'
          : 'border-grey-800 hover:bg-grey-900 hover:border-grey-700'
      )}
      aria-pressed={isSelected}
      onClick={onClick}
      data-cy={TAGS.card}
    >
      <div className="flex items-center gap-2 mb-1">
        {showRoleBadge && contract.listingType === 'MSAAS' && (
          <span className="px-1.5 py-0.5 text-xs font-semibold rounded bg-primary-50 text-white">
            B2B
          </span>
        )}
        {showRoleBadge && contract.listingType === 'JOB' && (
          <span className="px-1.5 py-0.5 text-xs font-semibold rounded bg-grey-500 text-white">
            JOB
          </span>
        )}
        {showRoleBadge && contract.listingType === 'SERVICE' && (
          <span className="px-1.5 py-0.5 text-xs font-semibold rounded bg-grey-500 text-white">
            SERVICE
          </span>
        )}
        <span className="text-xs text-grey-500">{contract.contractNumber}</span>
        <div className="ml-auto">
          <ContractStatusBadge status={contract.status} />
        </div>
      </div>
      <p className="text-sm font-bold text-grey-50 mb-0.5 truncate">
        {contract.compType ? `${contract.compType} Contract` : 'Contract'}
      </p>
      <p className="text-xs text-grey-500 mb-1">
        {dateStringFormatter(contract.contractStartDate, 'NUMERIC')}
        {contract.contractEndDate
          ? ` – ${dateStringFormatter(contract.contractEndDate, 'NUMERIC')}`
          : ' – Open-ended'}
      </p>
      <div className="flex justify-between items-center">
        <span className="text-sm font-medium text-grey-50">
          {contract.totalContractValue
            ? `$${Number(contract.totalContractValue).toLocaleString()}`
            : contract.hourlyRate
              ? `$${contract.hourlyRate}/hr`
              : ''}
        </span>
        {!isProviderView && contract.numOfFtes != null && (
          <span className="text-xs text-grey-500">{contract.numOfFtes} FTEs</span>
        )}
      </div>
      {showRoleBadge && currentEmployerId != null && (
        <div className="flex justify-end mt-1">
          <Pill label={role} type={role === 'Hiring' || role === 'Buyer' ? 'success' : 'warning'} />
        </div>
      )}
    </button>
  );
};
